import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SEO from "./SEO.jsx";
import { IS_FRANCE_MARKET } from "../utils/market.js";

/* ── Shared shell for /terms and /privacy ──────────────────────────────
   These documents are only maintained in English, Armenian and (for the
   France market) French (per product decision — not run through the
   general i18n resource files, since embedding ~3000 words of legal text
   per language as translation keys would bloat resources.js for no
   benefit). The page defaults to French on the France market, Armenian if
   that's the site's active language, otherwise English, and offers its
   own toggle so a visitor can read any available version regardless of
   the site's current language. A French translation carries its own
   market-specific corrections (GDPR digital-consent age, EU consumer-
   rights language) that the EN/HY text doesn't need — see Terms.jsx/
   Privacy.jsx's sectionsFr for details — so French is deliberately never
   silently substituted with the English text; if sectionsFr isn't passed
   for a given page, only EN/HY are offered there. */
function LegalPage({ titleEn, titleHy, titleFr, updatedEn, updatedHy, updatedFr, sectionsEn, sectionsHy, sectionsFr, seoTitleKey, seoDescKey, seoKeywordsKey, seoPath }) {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState(() => {
    if (IS_FRANCE_MARKET && sectionsFr) return "fr";
    return i18n.language === "hy" ? "hy" : "en";
  });

  const title = lang === "fr" ? titleFr : lang === "hy" ? titleHy : titleEn;
  const updated = lang === "fr" ? updatedFr : lang === "hy" ? updatedHy : updatedEn;
  const sections = lang === "fr" ? sectionsFr : lang === "hy" ? sectionsHy : sectionsEn;

  return (
    <div className="lg-root">
      {seoTitleKey && (
        <SEO titleKey={seoTitleKey} descriptionKey={seoDescKey} keywordsKey={seoKeywordsKey} path={seoPath} breadcrumbLabel={titleEn} />
      )}
      <style>{`
        .lg-root {
          background: var(--surface-50, #E7EFE4);
          min-height: 100vh;
          padding: 48px 5% 96px;
        }
        .lg-shell {
          max-width: 840px;
          margin: 0 auto;
        }
        .lg-toggle {
          display: inline-flex;
          gap: 4px;
          background: var(--surface-white, #F3F8EF);
          border-radius: 999px;
          padding: 4px;
          box-shadow: var(--clay-shadow-raised-sm, 0 4px 12px rgba(0,0,0,0.08));
          margin-bottom: 24px;
        }
        .lg-toggle button {
          border: none;
          background: transparent;
          padding: 7px 18px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          color: var(--ink-600, #5a7060);
          font-family: inherit;
        }
        .lg-toggle button.active {
          background: var(--green-700, #3A6B54);
          color: #fff;
        }
        .lg-title {
          font-family: var(--font-display, inherit);
          font-size: clamp(28px, 4vw, 40px);
          font-weight: 800;
          color: var(--ink-900, #1A382D);
          margin: 0 0 8px;
          line-height: 1.15;
        }
        .lg-updated {
          font-size: 13px;
          color: var(--ink-400, #7c9186);
          margin: 0 0 32px;
        }
        .lg-toc {
          background: var(--surface-white, #F3F8EF);
          border-radius: 16px;
          padding: 20px 24px;
          margin-bottom: 40px;
          box-shadow: var(--clay-shadow-raised-sm, 0 4px 12px rgba(0,0,0,0.06));
        }
        .lg-toc h2 {
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--ink-400, #7c9186);
          margin: 0 0 12px;
          font-weight: 700;
        }
        .lg-toc ol {
          margin: 0;
          padding-left: 20px;
          columns: 2;
          column-gap: 24px;
        }
        .lg-toc li { break-inside: avoid; margin-bottom: 6px; }
        .lg-toc a {
          color: var(--green-700, #3A6B54);
          text-decoration: none;
          font-size: 13.5px;
          font-weight: 600;
        }
        .lg-toc a:hover { text-decoration: underline; }
        .lg-section { margin-bottom: 36px; scroll-margin-top: 90px; }
        .lg-section h2 {
          font-family: var(--font-display, inherit);
          font-size: 20px;
          font-weight: 800;
          color: var(--ink-900, #1A382D);
          margin: 0 0 12px;
        }
        .lg-section h3 {
          font-size: 15px;
          font-weight: 700;
          color: var(--ink-900, #1A382D);
          margin: 18px 0 8px;
        }
        .lg-section p {
          font-size: 14.5px;
          line-height: 1.7;
          color: var(--ink-700, #3f5449);
          margin: 0 0 12px;
        }
        .lg-section ul {
          margin: 0 0 12px;
          padding-left: 22px;
        }
        .lg-section li {
          font-size: 14.5px;
          line-height: 1.7;
          color: var(--ink-700, #3f5449);
          margin-bottom: 6px;
        }
        .lg-section strong { color: var(--ink-900, #1A382D); }
        .lg-contact {
          background: var(--green-900, #1A382D);
          border-radius: 20px;
          padding: 28px 30px;
          margin-top: 48px;
        }
        .lg-contact p { color: rgba(245,247,240,0.85); margin: 0 0 6px; }
        .lg-contact a { color: #fff; font-weight: 700; text-decoration: none; }
        .lg-contact a:hover { text-decoration: underline; }
        @media (max-width: 640px) {
          .lg-toc ol { columns: 1; }
          .lg-contact { padding: 22px 20px; }
        }
      `}</style>

      <div className="lg-shell">
        <div className="lg-toggle">
          <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>English</button>
          <button className={lang === "hy" ? "active" : ""} onClick={() => setLang("hy")}>Հայերեն</button>
          {sectionsFr && <button className={lang === "fr" ? "active" : ""} onClick={() => setLang("fr")}>Français</button>}
        </div>

        <h1 className="lg-title">{title}</h1>
        <p className="lg-updated">{updated}</p>

        <nav className="lg-toc" aria-label="Table of contents">
          <h2>{lang === "fr" ? "Sommaire" : lang === "hy" ? "Բովանդակություն" : "Contents"}</h2>
          <ol>
            {sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`}>{s.heading}</a></li>
            ))}
          </ol>
        </nav>

        {sections.map((s) => (
          <section id={s.id} className="lg-section" key={s.id}>
            <h2>{s.heading}</h2>
            {s.body}
          </section>
        ))}

        <div className="lg-contact">
          <p>{lang === "fr" ? "Des questions ? Contactez-nous." : lang === "hy" ? "Հարցերի դեպքում կապվեք մեզ հետ." : "Questions? Contact us."}</p>
          <a href="mailto:greenwicharmenia@gmail.com">greenwicharmenia@gmail.com</a>
        </div>
      </div>
    </div>
  );
}

export default LegalPage;
